import { MoreHorizontal, Star, Trash2 } from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { Button } from "@/components/ui/button"
import { Combobox } from "@/components/ui/combobox"
import { Dialog } from "@/components/ui/dialog"
import { IconButton } from "@/components/ui/icon-button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/popover"

import {
  toPersonPickerOption,
  type ClientMember,
  type ClientRow,
  type ClientUserOption,
  type UserId,
} from "./clients-data"
import { GrantAccessDialog, RevokeAccessDialog } from "./clients-dialogs"

export interface MemberActions {
  onAddMember: (userId: UserId) => Promise<void>
  onRemoveMember: (userId: UserId) => Promise<void>
  onMakePrimary: (userId: UserId) => void
}

export function ExpandedMembers({
  row,
  users,
  actions,
  header,
}: {
  row: ClientRow
  users: readonly ClientUserOption[]
  actions: MemberActions
  header?: React.ReactNode
}): JSX.Element {
  const [pick, setPick] = React.useState<string | null>(null)
  const [pendingRemove, setPendingRemove] = React.useState<ClientMember | null>(null)
  const [pendingAdd, setPendingAdd] = React.useState<UserId | null>(null)
  const [isGrantingAccess, setIsGrantingAccess] = React.useState(false)
  const [isRevokingAccess, setIsRevokingAccess] = React.useState(false)
  const pickerHelpTextId = React.useId()

  const memberIds = React.useMemo(() => new Set(row.members.map((m) => m.userId)), [row.members])
  const options = users.filter((u) => !memberIds.has(u.id)).map(toPersonPickerOption)

  const pendingAddUser = pendingAdd ? (users.find((u) => u.id === pendingAdd) ?? null) : null

  const confirmGrantAccess = async (userId: UserId) => {
    setIsGrantingAccess(true)
    const grantSucceeded = await actions.onAddMember(userId).then(
      () => true,
      () => false,
    )
    setIsGrantingAccess(false)
    if (grantSucceeded) {
      setPendingAdd(null)
      setPick(null)
    }
  }

  const confirmRevokeAccess = async (userId: UserId) => {
    setIsRevokingAccess(true)
    const revokeSucceeded = await actions.onRemoveMember(userId).then(
      () => true,
      () => false,
    )
    setIsRevokingAccess(false)
    if (revokeSucceeded) setPendingRemove(null)
  }

  return (
    <div className="border-l-2 border-brand/40 bg-brand-tint/15 py-4 pl-[60px] pr-6">
      {header && <div className="mb-4">{header}</div>}
      {row.members.length === 0 ? (
        <p className="text-body-sm text-text-3">
          No members yet. A client login gains access to this engagement only after being added
          here.
        </p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {row.members.map((mem) => (
            <MemberRow
              key={mem.userId}
              engagement={row}
              member={mem}
              onMakePrimary={() => {
                actions.onMakePrimary(mem.userId)
              }}
              onRemove={() => {
                setPendingRemove(mem)
              }}
            />
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center gap-2">
        <div className="min-w-0 flex-1 sm:max-w-[360px]">
          <Combobox
            options={options}
            value={pick}
            onValueChange={setPick}
            placeholder={
              options.length === 0 ? "Every client account is a member" : "Add a client contact"
            }
            searchPlaceholder="Search contacts"
            disabled={options.length === 0}
            aria-label={`Add contact to ${row.name}`}
            aria-describedby={pickerHelpTextId}
          />
        </div>
        <Button
          size="sm"
          disabled={!pick || isGrantingAccess}
          onClick={() => {
            setPendingAdd(pick as UserId)
          }}
        >
          Grant access
        </Button>
      </div>
      <p id={pickerHelpTextId} className="mt-1.5 text-caption leading-normal text-text-3">
        Invited contacts can be added once they accept their invitation.
      </p>

      <Dialog
        open={pendingAdd !== null}
        onOpenChange={(open) => {
          if (!open && !isGrantingAccess) setPendingAdd(null)
        }}
      >
        {pendingAddUser && (
          <GrantAccessDialog
            engagement={row}
            user={pendingAddUser}
            isSubmitting={isGrantingAccess}
            onConfirm={() => void confirmGrantAccess(pendingAddUser.id)}
          />
        )}
      </Dialog>

      <Dialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open && !isRevokingAccess) setPendingRemove(null)
        }}
      >
        {pendingRemove && (
          <RevokeAccessDialog
            engagement={row}
            member={pendingRemove}
            isSubmitting={isRevokingAccess}
            onConfirm={() => void confirmRevokeAccess(pendingRemove.userId)}
          />
        )}
      </Dialog>
    </div>
  )
}

function MemberRow({
  engagement,
  member,
  onMakePrimary,
  onRemove,
}: {
  engagement: ClientRow
  member: ClientMember
  onMakePrimary: () => void
  onRemove: () => void
}): JSX.Element {
  return (
    <li className="flex items-center gap-2.5 rounded-md border border-border-soft bg-surface px-3 py-2">
      <Avatar name={member.name} identity={member.userId} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-body-sm font-medium text-text">{member.name}</span>
          {member.isPrimary && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-brand-tint px-1.5 py-px text-micro font-semibold text-brand"
              title="Marked as the client's main contact."
            >
              <Star className="size-2.5 fill-current" aria-hidden="true" />
              Main contact
            </span>
          )}
        </div>
        <div className="truncate font-id text-caption text-text-3">{member.email}</div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            size="sm"
            variant="ghost"
            label={`Actions for ${member.name}`}
            icon={<MoreHorizontal />}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!member.isPrimary && (
            <DropdownMenuItem icon={<Star />} onSelect={onMakePrimary}>
              Mark as main contact
            </DropdownMenuItem>
          )}
          <DropdownMenuItem icon={<Trash2 />} destructive onSelect={onRemove}>
            Revoke access from {engagement.name}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  )
}
